/* import __COLOCATED_TEMPLATE__ from './filterable-list.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import {
  ALL_CONTENT_COLUMN_DATA,
  type ColumnData,
  DEFAULT_SELECTABLE_COLUMNS,
  KNOWLEDGE_HUB_ALL_CONTENT_DEFAULT_COLUMNS,
  IMPORT_SOURCE_STATUSES,
  KNOWLEDGE_HUB_REQUIRED_COLUMNS,
  CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
  FOLDER_MULTILINGUAL_DEFAULT_COLUMNS,
  type SortDirection,
  type TagOperator,
  KNOWLEDGE_HUB_EVERY_CONTENT_COLUMN_ORDER,
} from 'embercom/lib/knowledge-hub/constants';

import { type TableColumn } from 'embercom/lib/knowledge-hub/constants';
import type IntlService from 'ember-intl/services/intl';
import type KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';
import { type Predicate } from 'embercom/lib/knowledge-hub/constants';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import {
  BULK_ACTION_PROGRESSION_ID,
  type BulkActionProgressionEvent,
  BulkActionProgressionValues,
} from 'embercom/services/knowledge-hub-service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import { type EntityType } from 'embercom/models/data/entity-types';
import type Folder from 'embercom/models/content-service/folder';
import { action } from '@ember/object';
import {
  type SyncIssuesFilterOptions,
  type FolderFilterOption,
} from 'embercom/lib/content-service/search-api';
import { isEmpty } from '@ember/utils';
import { type TaskGenerator } from 'ember-concurrency';
import { restartableTask, task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { timeout } from 'ember-concurrency';
import {
  IMPORT_JOB_STATUS_EVENT_ID,
  IMPORT_JOB_DELETE_STATUS_ID,
  type UpdateImportSourceStatusEvent,
  type ImportServiceImportSourceDeletedEvent,
} from 'embercom/services/knowledge-hub-service';
import type ContentFragment from 'embercom/models/content-service/content-fragment';
import { SyncBehaviorType } from 'embercom/models/import-service/import-source';
import { SyncStatus } from 'embercom/models/knowledge-hub/sync-source-wrapper';
import type Router from '@ember/routing/router-service';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import type KnowledgeHubContentWrapper from 'embercom/models/content-service/knowledge-hub-content-wrapper';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { ContentFilter } from './overview';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type ContentImportLogsModalService from 'embercom/services/content-import-logs-modal-service';
import { SourceType, SourceTypeName } from 'embercom/models/import-service/import-source';
import {
  findRootSyncedFolder,
  getSyncSourceForFolder,
} from 'embercom/utils/knowledge-hub/sync-source-helpers';
import { createTreeItem } from './folders/tree-helper';
import { hasFeature } from 'embercom/helpers/has-feature';
import { RUN_STATUSES } from 'embercom/lib/external-content/constants';
import {
  CONTENT_IMPORT_RUN_STATUS_TOPIC,
  EVENT_SERVICE_ID,
  FIN_INGESTION_POLLING_INTERVAL,
  SYNC_SUMMARY_POLL_INTERVAL,
} from 'embercom/services/content-import-service';
import moment from 'moment-timezone';
import type ContentImportSource from 'embercom/models/content-service/content-import-source';
import { saveAdvancedSettings } from 'embercom/utils/knowledge-hub/advanced-settings-helpers';

export const AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID =
  'AiV3ContentIngestionJobStatusChanged';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    title: string;
    folder?: Folder;
    setSearchTerm: (searchTerm?: string) => void;
    searchTerm?: string;
    setLinkedUrls: (linkedUrls?: string[]) => void;
    linkedUrls?: string[];
    titleTerm?: string;
    setTitleTerm: (titleTerm?: string) => void;
    bodyTerm?: string;
    setBodyTerm: (bodyTerm?: string) => void;
    urlTerm?: string;
    setUrlTerm: (urlTerm?: string) => void;
    listApi: KnowledgeHubApi;
    locale: string | null;
    setLocale: (locale: string | null) => void;
    predicates?: Array<Predicate>;
    setPredicates: (predicates: Array<Predicate>) => void;
    status: string | null;
    setStatus: (status: string | null) => void;
    chatbotState?: string;
    setChatbotState: (chatbotState?: string) => void;
    copilotState?: string;
    setCopilotState: (copilotState?: string) => void;
    salesAgentState?: string;
    setSalesAgentState: (salesAgentState?: string) => void;
    createdBy?: string;
    setCreatedBy: (createdBy?: string) => void;
    selectedTypes?: Array<EntityType>;
    setSelectedTypes: (selectedTypes?: Array<EntityType>) => void;
    urlSourceId?: string;
    setUrlSourceId: (urlSourceId?: string) => void;
    selectedHelpCenterIds?: Array<string>;
    setSelectedHelpCenterIds: (selectedHelpCenterIds?: Array<string>) => void;
    lastUpdatedBy?: string;
    setLastUpdatedBy: (lastUpdatedBy?: string) => void;
    sortDirection?: SortDirection;
    sortBy?: string;
    setSortBy: (sortBy?: string) => void;
    setSortDirection: (sortDirection?: string) => void;
    displayFolderFilter?: boolean;
    selectedFolderFilterOption?: FolderFilterOption;
    setFolderFilterOption?: (folderFilterOption?: FolderFilterOption) => void;
    selectedSyncIssueOption?: SyncIssuesFilterOptions;
    setSyncIssueFilterOption?: (syncIssueOption?: SyncIssuesFilterOptions) => void;
    selectableColumns?: ColumnData[];
    resetFilters: () => void;
    activeFilter?: string;
    setActiveFilter: (filter?: string) => void;
    filtersApplied?: string[];
    setFiltersApplied: (filters: string[]) => void;
    tagIds?: string[];
    setTagIds: (tagIds?: string[]) => void;
    tagOperator?: TagOperator;
    setTagOperator: (tagOperator?: TagOperator) => void;
    collectionIds?: string[];
    setCollectionIds: (collectionIds?: string[]) => void;
    writtenByIds?: string[];
    setWrittenByIds: (writtenByIds?: string[]) => void;
    aiContentSegmentIds?: string[];
    setAiContentSegmentIds: (aiContentSegmentIds?: string[]) => void;
    helpCenterAudienceRoles?: string[];
    setHelpCenterAudienceRoles?: (roles?: string[]) => void;
    nativeContentPage?: boolean;
    shouldShowArticlesFolder?: boolean;
    // Routes passed down from route templates for folder navigation.
    // These are passed through this component to FilterableList → List → ListRow → Title
    parentFolderRoute?: string;
    allContentRoute?: string;
  };
}

export const SHOW_ALL_TRANSLATIONS = 'knowledge_hub_all_content_show_all_translations';

export enum SyncSteps {
  Connect = 'connect',
  ConfluenceSync = 'confluence-sync',
  GuruSync = 'guru-sync',
  NotionSync = 'notion-sync',
  SalesforceSync = 'salesforce-sync',
  BoxSync = 'box-sync',
  Document360Sync = 'document360-sync',
}

export const KNOWLEDGE_HUB_ALL_CONTENT_COLUMNS = 'knowledge_hub_all_content_columns';
export const KNOWLEDGE_HUB_ALL_CONTENT_COLUMNS_LAST_UPDATED_AT =
  'knowledge_hub_all_content_columns_last_updated_at';
export const CONTENT_PERFORMANCE_ROUTE = 'apps.app.reports.customer-support.fin-ai-agent';
export const AI_AGENT_SETUP_ROUTE = 'apps.app.automation.fin-ai-agent.setup';
export const SPACER_COLUMN: TableColumn = { valuePath: 'spacer' };

// Unix timestamp for 15th November 2024 12:00:00 UTC
// A time before this PR was merged, so that we can test the new columns and not break navbar beta users over the weekend
export const HELP_CENTER_COLUMN_DEFAULT_CUTOFF_DATE = 1731672000;

// Threshold for determining when to reload entire list vs just update count display
const SMALL_LIST_RELOAD_THRESHOLD = 25;

export default class FilterableList extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intercomEventService: $TSFixMe;
  @service declare realTimeEventService: { on: Function; off: Function; subscribeTopics: Function };
  @service declare notificationsService: $TSFixMe;
  @service declare router: Router;
  @service declare permissionsService: $TSFixMe;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare modalService: $TSFixMe;
  @service declare contentImportLogsModalService: ContentImportLogsModalService;

  @tracked showAllToggled: boolean;
  @tracked step?: SyncSteps;
  @tracked showCreateFolderModal = false;
  @tracked showNewContentModal = false;
  @tracked showFileUploadModal = false;

  @tracked selectedFragments: Set<ContentFragment> = new Set();
  @tracked unselectedFragments: Set<ContentFragment> = new Set();
  @tracked isBulkSelection = false;
  @tracked isContentReportingDropdownOpen = false;
  @tracked syncProgressText = '';
  @tracked previousImportedItemsCount: number | null = null;
  @tracked importedItemsCount: number | null = null;
  @tracked hasSyncProgressData = false;
  @tracked aiV3ContentIngestionReloadTimer: any = null;

  @trackedInLocalStorage({
    keyName: KNOWLEDGE_HUB_ALL_CONTENT_COLUMNS,
  })
  columnsLocalStorage: string | undefined;

  @trackedInLocalStorage({
    keyName: KNOWLEDGE_HUB_ALL_CONTENT_COLUMNS_LAST_UPDATED_AT,
  })
  columnsLastUpdatedAtLocalStorage: number | undefined;

  @tracked columnsWhenFiltering = this.columns;

  aiAgentContentFilter = ContentFilter.AiAgent;

  constructor(owner: unknown, args: any) {
    super(owner, args);

    let showAllTranslations = localStorage.getItem(SHOW_ALL_TRANSLATIONS);
    this.showAllToggled = showAllTranslations === 'true';
    this.knowledgeHubService.subscribeToImportServiceUpdates();
    this.setImportServiceUpdateActions();
    this.setBulkActionProgressActions();
    this.aiAgentSetupService.loadFinWorkflowsCount();
    this.aiAgentSetupService.loadSetupRulesets();

    if (args.aiContentSegmentIds) {
      if (args.aiContentSegmentIds[0] === 'default') {
        this.args.setAiContentSegmentIds([]);
      } else {
        this.args.setAiContentSegmentIds(args.aiContentSegmentIds);
        let currentFilters = args.filtersApplied || [];
        if (!currentFilters.includes('aiContentSegmentIds')) {
          this.args.setFiltersApplied([...currentFilters, 'aiContentSegmentIds']);
        }
      }
    }

    if (hasFeature('team-knowledge-foundations-website-sync-progress', this.appService)) {
      this.subscribeToImportEvents();
      this.subscribeToAiV3ContentIngestionEvents();

      if (this.args.folder?.firstTimeSyncing) {
        this.updateSyncProgressTextAndRefreshList();
        this.startPolling();
      }
    }
  }

  private subscribeToImportEvents() {
    this.realTimeEventService.subscribeTopics([CONTENT_IMPORT_RUN_STATUS_TOPIC]);
    this.realTimeEventService.on(EVENT_SERVICE_ID, this, 'handleImportStatusChange');
  }

  private subscribeToAiV3ContentIngestionEvents() {
    this.realTimeEventService.on(
      AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID,
      this,
      'handleAiV3ContentIngestionJobStatusChange',
    );
  }

  private unsubscribeFromImportEvents() {
    this.realTimeEventService.off(EVENT_SERVICE_ID, this, 'handleImportStatusChange');
  }

  private unsubscribeFromAiV3ContentIngestionEvents() {
    this.realTimeEventService.off(
      AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID,
      this,
      'handleAiV3ContentIngestionJobStatusChange',
    );
  }

  // @ts-ignore - Used as event handler callback in subscribeToImportEvents
  private async handleImportStatusChange(event: any) {
    if (event.event_type === RUN_STATUSES.completed || event.event_type === RUN_STATUSES.failed) {
      if (this.syncSource && this.syncSource.sourceId === event.content_import_source_id) {
        this.syncSource.status =
          event.event_type === RUN_STATUSES.completed ? SyncStatus.SYNCED : SyncStatus.ERROR;
      }

      if (this.args.folder?.firstTimeSyncing) {
        await taskFor(this.reloadList).perform();
        this.stopPolling();
        // perform delayed reload for ai v3 ingestion
        this.performDelayedReload();
      }
    }
  }

  // @ts-ignore - Used as event handler callback in subscribeToAiV3ContentIngestionEvents
  private handleAiV3ContentIngestionJobStatusChange(event: any) {
    if (this.aiV3ContentIngestionReloadTimer) {
      clearTimeout(this.aiV3ContentIngestionReloadTimer);
      this.performDelayedReload();
    }
  }

  private performDelayedReload() {
    this.aiV3ContentIngestionReloadTimer = setTimeout(() => {
      taskFor(this.reloadList).perform();
      this.aiV3ContentIngestionReloadTimer = null;
    }, FIN_INGESTION_POLLING_INTERVAL);
  }

  private startPolling() {
    if (!taskFor(this.pollSyncProgressAndRefreshList).isRunning) {
      taskFor(this.pollSyncProgressAndRefreshList).perform();
    }
  }

  private stopPolling() {
    taskFor(this.pollSyncProgressAndRefreshList).cancelAll();
  }

  @task({ restartable: true, maxConcurrency: 1 })
  private *pollSyncProgressAndRefreshList(): TaskGenerator<void> {
    while (true) {
      if (!this.args.folder?.firstTimeSyncing || !this.args.folder?.contentImportSourceId) {
        break;
      }

      yield timeout(SYNC_SUMMARY_POLL_INTERVAL);

      yield this.updateSyncProgressTextAndRefreshList();
    }
  }

  private async updateSyncProgressTextAndRefreshList(): Promise<void> {
    try {
      if (!this.shouldContinuePolling()) {
        this.clearSyncProgressData();
        return;
      }

      let syncSummary = await this.fetchSyncSummary();
      if (!syncSummary) {
        this.clearSyncProgressData();
        return;
      }

      let { importedCount, availableCount } = this.extractCounts(syncSummary);

      await this.reloadListIfNeeded(importedCount);
      this.updateProgressState(importedCount, availableCount);
      this.updateProgressText(importedCount, availableCount);
    } catch (error) {
      this.setFallbackProgressState();
    }
  }

  private shouldContinuePolling(): boolean {
    return !!(this.args.folder?.contentImportSourceId && this.args.folder?.firstTimeSyncing);
  }

  private clearSyncProgressData(): void {
    this.syncProgressText = '';
    this.hasSyncProgressData = false;
  }

  private async fetchSyncSummary() {
    if (!this.args.folder?.contentImportSourceId) {
      return null;
    }

    return await this.knowledgeHubService.getSyncSummary(
      String(this.args.folder.contentImportSourceId),
    );
  }

  private extractCounts(response: any): { importedCount: number; availableCount: number } {
    let importedCount = response?.[0]?.imported ?? 0;
    let availableCount = response?.[0]?.available ?? 0;
    return { importedCount, availableCount };
  }

  private async reloadListIfNeeded(importedCount: number): Promise<void> {
    let hasTotalCountChanged =
      this.previousImportedItemsCount !== null && this.previousImportedItemsCount !== importedCount;

    if (!hasTotalCountChanged) {
      return;
    }

    if (this.args.listApi.totalCount <= SMALL_LIST_RELOAD_THRESHOLD) {
      this.importedItemsCount = null;
      await taskFor(this.reloadList).perform();
    } else {
      // For large lists, just update the count display instead of reloading
      this.importedItemsCount = importedCount;
    }
  }

  private updateProgressState(importedCount: number, availableCount: number): void {
    this.previousImportedItemsCount = importedCount;
    this.hasSyncProgressData = availableCount > 0;
  }

  private updateProgressText(importedCount: number, availableCount: number): void {
    this.syncProgressText =
      availableCount > 0
        ? this.intl.t('knowledge-hub.sync-progress-count', {
            imported: importedCount,
            available: availableCount,
          })
        : this.intl.t('knowledge-hub.sync-progress');
  }

  private setFallbackProgressState(): void {
    this.syncProgressText = this.intl.t('knowledge-hub.sync-progress');
    this.hasSyncProgressData = false;
  }

  async willDestroy(): Promise<void> {
    super.willDestroy();
    this.destroyNexusEvents();

    if (hasFeature('team-knowledge-foundations-website-sync-progress', this.appService)) {
      if (this.aiV3ContentIngestionReloadTimer) {
        clearTimeout(this.aiV3ContentIngestionReloadTimer);
        this.aiV3ContentIngestionReloadTimer = null;
      }

      this.unsubscribeFromAiV3ContentIngestionEvents();
      this.unsubscribeFromImportEvents();
      if (this.args.folder?.firstTimeSyncing) {
        this.stopPolling();
      }
    }
  }

  setImportServiceUpdateActions() {
    this.realTimeEventService.on(
      IMPORT_JOB_STATUS_EVENT_ID,
      this,
      'updateImportSourceStatusAndNotify',
    );
    this.realTimeEventService.on(
      IMPORT_JOB_DELETE_STATUS_ID,
      this,
      'resetFiltersAndNotifyDeletion',
    );
  }

  setBulkActionProgressActions() {
    this.realTimeEventService.on(BULK_ACTION_PROGRESSION_ID, this, 'handleBulkActionProgression');
  }

  private destroyNexusEvents() {
    this.realTimeEventService.off(BULK_ACTION_PROGRESSION_ID, this, 'handleBulkActionProgression');
  }

  async updateImportSourceStatusAndNotify(event: UpdateImportSourceStatusEvent) {
    let source = this.store.peekRecord('import-service/import-source', event.import_source_id);
    if (!source) {
      return;
    }

    source.status = event.status;

    if (event.status === IMPORT_SOURCE_STATUSES.success) {
      if (event.sync_behavior === SyncBehaviorType.Sync) {
        this.knowledgeHubService.notifySourceStateChangeConfirmation(
          this.intl.t('knowledge-hub.filterable-list.sources.nexus-event-sync-content-success'),
          `${event.import_source_id}`,
        );
      }

      if (event.sync_behavior === SyncBehaviorType.Import) {
        this.knowledgeHubService.notifySourceStateChangeConfirmation(
          this.intl.t('knowledge-hub.filterable-list.sources.import-complete', {
            sourceName: source.sourceName,
          }),
          `${event.import_source_id}`,
        );
      }

      this.resetFilters();
      this.hardReloadImportSources();
    }
  }

  async resetFiltersAndNotifyDeletion(event: ImportServiceImportSourceDeletedEvent) {
    if (event.is_deleted) {
      this.knowledgeHubService.notifySourceStateChangeConfirmation(
        this.intl.t('knowledge-hub.filterable-list.sources.nexus-event-delete-success'),
        `${event.import_source_id}`,
      );
      this.resetFilters();
      this.store.unloadAll('content-service/folder'); // otherwise deleted synced folders stick around
      let folderIds = (await this.knowledgeHubService.fetchFoldersOnce(true)).map(
        (folder) => folder.id,
      );

      if (this.args.folder && !folderIds.includes(this.args.folder?.id)) {
        // if the active folder isn't in the new list of folders it was deleted
        // so navigate to root folder
        this.router.transitionTo('apps.app.knowledge-hub.all-content');
      }
    } else {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.sources.nexus-event-delete-error'),
      );
    }
  }

  @action
  navigateToOverview() {
    if (this.appService.app.canUseStandalone) {
      this.router.transitionTo('apps.app.knowledge-hub.overview');
    }
  }

  async handleBulkActionProgression(event: BulkActionProgressionEvent) {
    let currentAdmin = this.appService.app.currentAdmin;
    if (currentAdmin.id !== event.admin_id.toString()) {
      return;
    }

    if (event.status === BulkActionProgressionValues.Complete) {
      if (event.all_entities_unaffected) {
        this.knowledgeHubService.notifyNoEntitiesAffected(
          event.bulk_action,
          this.appService.app.canUseStandalone,
        );
      } else {
        this.sendCompletionNotification(event);
        taskFor(this.reloadList).perform();
      }
    }

    this.knowledgeHubService.handleBulkActionFailure(event);
  }

  private async sendCompletionNotification(event: BulkActionProgressionEvent) {
    let action = event.bulk_action;
    let unaffectedEntityTypes = event.unaffected_entity_types;
    let language_code = event.language_code;
    let destinationFolderId = event.destination_folder_id;
    this.knowledgeHubService.notifyBulkActionCompleted(
      action,
      unaffectedEntityTypes,
      language_code,
      destinationFolderId,
      this.appService.app.canUseStandalone,
    );
    if (destinationFolderId) {
      await this.knowledgeHubService.fetchFoldersOnce(true);
    }
  }

  get contentPerformanceOptions() {
    return [
      {
        items: [
          {
            icon: 'column-chart',
            text: this.intl.t(
              'knowledge-hub.buttons.content-reporting.options.view-content-performance.title',
            ),
            description: this.intl.t(
              'knowledge-hub.buttons.content-reporting.options.view-content-performance.description',
            ),
            onSelectItem: () => this.openContentPerformancePage(),
          },
          {
            icon: 'help-space',
            text: this.intl.t(
              'knowledge-hub.buttons.content-reporting.options.view-unresolved-questions.title',
            ),
            description: this.intl.t(
              'knowledge-hub.buttons.content-reporting.options.view-unresolved-questions.description',
            ),
            onSelectItem: () => this.openUnresolvedQuestionsPage(),
          },
        ],
      },
    ];
  }

  openContentPerformancePage() {
    this.trackAnalyticsEvent('clicked', 'content_performance_button');
    let contentPerformancePageUrl = this.router.urlFor(CONTENT_PERFORMANCE_ROUTE);
    safeWindowOpen(contentPerformancePageUrl);
  }

  openUnresolvedQuestionsPage() {
    this.trackAnalyticsEvent('clicked', 'unresolved_questions_button');
    let unresolvedQuestionsPageUrl = this.router.urlFor(AI_AGENT_SETUP_ROUTE, {
      queryParams: { activeTab: 'optimize', previewFin: false },
    });
    safeWindowOpen(unresolvedQuestionsPageUrl);
  }

  get columnsToDisplay() {
    if (this.hasAppliedFilter) {
      return this.columnsWhenFiltering;
    }
    return this.columns;
  }

  get selectableColumns() {
    let columns = this.args.selectableColumns ?? DEFAULT_SELECTABLE_COLUMNS;
    if (!this.appService.app.canUseHelpCenterAudienceVisibility) {
      columns = columns.filter((col) => col.value !== 'helpCenterAudience');
    }
    return this.filterAndUpdateColumnTranslations(columns);
  }

  hardReloadImportSources() {
    this.store.findAll('import-service/import-source', { reload: true });
  }

  get columns(): TableColumn[] {
    let columns: TableColumn[];
    if (this.columnsLocalStorage) {
      columns = JSON.parse(this.columnsLocalStorage) as TableColumn[];
    } else if (this.args.folder) {
      let hasMultilingual = this.args.listApi.contentWrappers.any(
        (contentWrapper: KnowledgeHubContentWrapper) => contentWrapper?.isMultilingual,
      );
      columns = hasMultilingual ? FOLDER_MULTILINGUAL_DEFAULT_COLUMNS : this.defaultColumns;
    } else {
      columns = this.defaultColumns;
    }

    // Overwrite the columns if the user has last updated their columns before the cutoff date
    // See PR for more details
    if (
      this.columnsLastUpdatedAtLocalStorage === undefined ||
      this.columnsLastUpdatedAtLocalStorage < HELP_CENTER_COLUMN_DEFAULT_CUTOFF_DATE
    ) {
      let newColumns = [
        ALL_CONTENT_COLUMN_DATA.collectionNames,
        ALL_CONTENT_COLUMN_DATA.articlePublishedState,
        ALL_CONTENT_COLUMN_DATA.updatedAt,
        ALL_CONTENT_COLUMN_DATA.status,
      ];

      // Filter out duplicates based on valuePath
      columns = [
        ...columns,
        ...newColumns.filter(
          (newCol) => !columns.some((existingCol) => existingCol.valuePath === newCol.valuePath),
        ),
      ];

      // Sort based on KNOWLEDGE_HUB_EVERY_CONTENT_COLUMN order
      let columnOrder = KNOWLEDGE_HUB_EVERY_CONTENT_COLUMN_ORDER;
      columns.sort((a, b) => columnOrder.indexOf(a.valuePath) - columnOrder.indexOf(b.valuePath));
    }

    if (!this.appService.app.canUseHelpCenterAudienceVisibility) {
      columns = columns.filter((col) => col.valuePath !== 'helpCenterAudience');
    }

    columns = this.filterAndUpdateColumnTranslations(columns);

    // We need to ensure that the HC sign post component is rendered to customers who have visited this page and have columns stored in local storage
    // Otherwise, we'd need them to remove & re-add the column to see the HC sign post component
    let statusColumn = columns.find((column) => column.valuePath === 'status');
    if (statusColumn && !statusColumn.customHeaderComponent) {
      statusColumn.customHeaderComponent =
        'knowledge-hub/filterable-list/table/custom-column-headers/help-center-status';
    }

    // Filtering out the helpCenterName column if present in the local storage of users having saved this in their filter list
    let deprecatedColumnNames = ['helpCenterName'];
    columns = columns.filter((column) => !deprecatedColumnNames.includes(column.valuePath));

    // Add a spacer column to the end of the columns array
    columns.push(SPACER_COLUMN);

    return columns;
  }

  set columns(columns: TableColumn[]) {
    this.columnsLocalStorage = JSON.stringify(columns);
    this.columnsLastUpdatedAtLocalStorage = Math.floor(Date.now() / 1000); // Convert to seconds
  }

  private filterAndUpdateColumnTranslations<T extends TableColumn | ColumnData>(columns: T[]): T[] {
    return this.updateColumnTranslations(this.filterColumns(columns));
  }

  private updateColumnTranslations<T extends TableColumn | ColumnData>(columns: T[]): T[] {
    return columns.map((column) => {
      let updatedColumn = { ...column };
      let valuePath = 'valuePath' in column ? column.valuePath : column.value;

      // Rename "AI Agent" to "Fin Service Agent" when Sales Agent or Ecommerce Agent column is visible
      if (
        (this.appService.app.canUseFinSalesAgent || this.appService.app.canUseFinEcommerceAgent) &&
        valuePath === 'chatbotState'
      ) {
        updatedColumn.labelIntlKey =
          'knowledge-hub.filterable-list.list.columns.chatbot-state-with-sales-agent';
      }

      if (this.appService.app.canUseStandalone) {
        if (valuePath === 'copilotState') {
          // Use Fin Copilot instead of Copilot for standalone apps
          updatedColumn.labelIntlKey =
            'knowledge-hub.filterable-list.list.columns.copilot-state-standalone';
        }

        if (valuePath === 'chatbotState') {
          if (this.appService.app.canUseCopilotOnStandalone) {
            // Use Fin Agent instead of AI Agent for standalone apps
            updatedColumn.labelIntlKey =
              'knowledge-hub.filterable-list.list.columns.chatbot-state-standalone';
          } else {
            // With copilot disabled, we want to just show Enabled the full AI Agent name instead of the simplified name
            updatedColumn.labelIntlKey =
              'knowledge-hub.filterable-list.list.columns.chatbot-state-standalone-without-copilot';
          }
        }

        if (valuePath === 'segment') {
          // Use Audience instead of Fin audience for standalone apps
          updatedColumn.labelIntlKey =
            'knowledge-hub.filterable-list.list.columns.segment-standalone';
        }
      }

      return updatedColumn;
    });
  }

  // This function filters out of the TableColumns or ColumnData any columns that are not relevant to the current app
  // due to feature flags.
  private filterColumns<T extends TableColumn | ColumnData>(columns: T[]): T[] {
    let columnsToFilterOut: string[] = [];
    if (!this.appService.app.canUseExternalAi) {
      columnsToFilterOut = ['chatbotState', 'copilotState', 'salesAgentState', 'ecommerceState'];
    }

    if (this.appService.app.canUseStandalone) {
      columnsToFilterOut = [
        'status',
        'articlePublishedState',
        'collectionNames',
        'folder',
        'syncStatus',
        'helpCenterState',
      ];

      if (!this.appService.app.canUseCopilotOnStandalone) {
        columnsToFilterOut.push('copilotState');
      }
    }

    // Web folder specific filtering
    if (this.isWebSourceFolder) {
      columnsToFilterOut.push('collectionNames', 'articlePublishedState', 'writtenBy');
      if (!this.appService.app.canUseUnifiedContentUI) {
        columnsToFilterOut.push('syncStatus');
      }
    } else {
      columnsToFilterOut.push('syncStatus');
    }

    // Only required on all-content page - not selectable
    columnsToFilterOut.push('helpCenterState');

    if (!this.appService.app.canUseFinSalesAgent) {
      columnsToFilterOut.push('salesAgentState');
    }

    if (!this.appService.app.canUseFinEcommerceAgent) {
      columnsToFilterOut.push('ecommerceState');
    }

    return columns.filter(
      (column) =>
        !columnsToFilterOut.includes('valuePath' in column ? column.valuePath : column.value),
    );
  }

  get isWebSourceFolder(): boolean {
    return this.args.folder?.sourceTypeName === SourceTypeName[SourceType.Web];
  }

  get defaultColumns(): TableColumn[] {
    return KNOWLEDGE_HUB_ALL_CONTENT_DEFAULT_COLUMNS;
  }

  get parentFolder() {
    return this.args.folder?.parentFolder;
  }

  get subFoldersCount() {
    return this.args.folder?.orderedSubFolders.length || 0;
  }

  get isNewSubFolderDisabled() {
    return this.args.folder?.atMaxDepth();
  }

  get showNewContentButton() {
    return this.args.folder ? this.args.folder.isEditable : true;
  }

  get showManageSourcesButton() {
    let rootFolder = findRootSyncedFolder(this.args.folder);
    return rootFolder && getSyncSourceForFolder(this.store, rootFolder.id);
  }

  get hasAppliedFilter(): boolean {
    return (
      !isEmpty(this.args.searchTerm) ||
      !isEmpty(this.args.selectedTypes) ||
      !isEmpty(this.args.filtersApplied) ||
      !isEmpty(this.args.linkedUrls) ||
      !isEmpty(this.args.titleTerm) ||
      !isEmpty(this.args.bodyTerm) ||
      !isEmpty(this.args.urlTerm)
    );
  }

  get showEmptyState() {
    return (
      !this.args.listApi.isLoadingPage &&
      this.args.listApi.totalCount === 0 &&
      !this.hasAppliedFilter
    );
  }

  get treeItem() {
    if (this.args.folder) {
      return this.args.folder.treeItem ?? this.createFallbackTreeItem(this.args.folder);
    }
    return (
      this.knowledgeHubService.nativeContentTreeItem ||
      this.knowledgeHubService.tree?.children.firstObject
    );
  }

  private createFallbackTreeItem(folder: Folder) {
    let tree = this.knowledgeHubService.tree;
    let parent = folder.parentFolder?.treeItem || tree;
    return createTreeItem(parent, folder, undefined, undefined, false);
  }

  @action resetFilters() {
    this.args.resetFilters();
    this.columnsWhenFiltering = this.columns;
    this.args.listApi.reset();
    taskFor(this.reloadPage).perform();
  }

  @action beforeSearch() {
    if (!this.args.listApi.isFiltering) {
      // This is the first filter / search being applied
      this.ensureFolderColumnVisible();
    }
  }

  @action ensureFolderColumnVisible() {
    let otherColumns = this.columnsWhenFiltering.reject(
      (column) =>
        column.valuePath === ALL_CONTENT_COLUMN_DATA.folder.valuePath ||
        column.valuePath === ALL_CONTENT_COLUMN_DATA.contentType.valuePath ||
        column.valuePath === SPACER_COLUMN.valuePath,
    );
    // Insert contentType column after title column
    let titleIndex = otherColumns.findIndex(
      (column) => column.valuePath === ALL_CONTENT_COLUMN_DATA.title.valuePath,
    );
    let insertIndex = titleIndex !== -1 ? titleIndex + 1 : 0;
    otherColumns.splice(insertIndex, 0, ALL_CONTENT_COLUMN_DATA.contentType);
    this.columnsWhenFiltering = otherColumns.concat(ALL_CONTENT_COLUMN_DATA.folder, SPACER_COLUMN);
  }

  @restartableTask *reloadList(): TaskGenerator<void> {
    yield this.args.listApi.search(
      this.args.searchTerm,
      this.args.linkedUrls,
      this.args.bodyTerm,
      this.args.urlTerm,
    );
    yield this.args.listApi.refreshPageAfterDelay();
  }

  @restartableTask *reloadPage(): TaskGenerator<void> {
    yield this.args.listApi.loadPage();
  }

  get showAllTranslations() {
    return this.showAllToggled || this.args.listApi.requiresShowTranslations;
  }

  @action
  toggleTranslations() {
    this.trackAnalyticsEvent('clicked', 'toggle_translations_button');
    this.showAllToggled = !this.showAllToggled;
    this.args.listApi.requiresShowTranslations = false;
    localStorage.setItem(SHOW_ALL_TRANSLATIONS, this.showAllToggled ? 'true' : 'false');
  }

  @action
  closeSourceModal() {
    this.step = undefined;
    this.knowledgeHubService.removeEmptySources();
  }

  @action
  async openCreateFolderModal() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }
    this.trackAnalyticsEvent(
      'clicked',
      !this.args.folder ? 'new_folder_button' : 'new_subfolder_button',
    );

    this.showCreateFolderModal = true;
  }

  @action
  setSyncStep(step: SyncSteps) {
    this.step = step;
  }

  @action
  updatedSelectedTableColumns(columns: string[]) {
    let selectedColumns = columns.map((col) => ALL_CONTENT_COLUMN_DATA[col]);
    this.columns = KNOWLEDGE_HUB_REQUIRED_COLUMNS.concat(selectedColumns);
    this.columnsWhenFiltering = this.columns;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'update_columns',
      object: 'all_content',
      place: 'knowledge_hub',
    });
  }

  @action toggleNewContentModal(state = false): void {
    this.showNewContentModal = state;
    this.trackAnalyticsEvent('clicked', 'new_content_button');
  }

  @action
  setSelectedFragments(fragments: Set<ContentFragment>) {
    this.selectedFragments = fragments;
  }

  @action
  setUnselectedFragments(fragments: Set<ContentFragment>) {
    this.unselectedFragments = fragments;
    if (this.isBulkSelection && this.unselectedFragments.size > 0) {
      this.setIsBulkSelection(false);
    }
  }

  @action
  setIsBulkSelection(state: boolean) {
    this.isBulkSelection = state;
  }

  @action
  resetSelectedFragments() {
    this.selectedFragments = new Set();
    this.unselectedFragments = new Set();
    this.isBulkSelection = false;
  }

  @action openContentReportingDropdown() {
    this.isContentReportingDropdownOpen = !this.isContentReportingDropdownOpen;
    if (this.isContentReportingDropdownOpen) {
      this.trackAnalyticsEvent('opened', 'content_reporting_dropdown');
    }
  }

  private trackAnalyticsEvent(action: string, object: string, metadata?: any): void {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      section: 'knowledge_hub',
      context: 'all_content',
      place: 'all_content',
      ...metadata,
    });
  }

  get syncSource() {
    let rootFolder = findRootSyncedFolder(this.args.folder);
    if (!rootFolder) {
      return undefined;
    }
    return getSyncSourceForFolder(this.store, rootFolder.id);
  }

  get lastSyncText(): string | null {
    if (!this.syncSource?.lastSyncedAt) {
      return '';
    }
    let lastSyncDate = moment(this.syncSource.lastSyncedAt).fromNow();
    return this.intl.t('knowledge-hub.last-synced', { date: lastSyncDate });
  }

  get shouldShowSyncProgress(): boolean {
    return (
      this.isWebSourceFolder &&
      this.syncSource?.isSyncing === true &&
      hasFeature('team-knowledge-foundations-website-sync-progress', this.appService)
    );
  }

  @action
  openReactContentImportLogsSheet() {
    this.contentImportLogsModalService.openContentImportLogsSheet(this.syncSource!);
  }

  @action
  async openAdvancedSettingsSheet(contentImportSource: ContentImportSource) {
    this.modalService.openModal(
      'react/knowledge-hub/advanced-settings-websync/advanced-settings-websync-sheet',
      {
        appId: this.appService.app.id,
        adminId: this.appService.app.currentAdmin.id,
        contentImportSource,
        onSave: async (formData: Record<string, unknown>) => {
          await this.saveAdvancedSettings(contentImportSource, formData, false);
        },
        onSaveAndSync: async (formData: Record<string, unknown>) => {
          await this.saveAdvancedSettings(contentImportSource, formData, true);
        },
      },
    );
  }

  private async saveAdvancedSettings(
    contentImportSource: ContentImportSource,
    formData: Record<string, unknown>,
    shouldResync: boolean,
  ) {
    await saveAdvancedSettings({
      contentImportSource,
      formData,
      shouldResync,
      intl: this.intl,
      notificationsService: this.notificationsService,
      onRefresh: async () => {
        await this.args.listApi.refreshPageAfterDelay();
      },
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList': typeof FilterableList;
    'knowledge-hub/filterable-list': typeof FilterableList;
  }
}
